import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { actionsApi } from '../api/actions'
import { portalAdminApi } from '../api/portalAdmin'
import { ApiError } from '../lib/api'
import type { ActionSharedState, ActionStatus, AdPlatform, Priority } from '../lib/types'
import { ACTION_STATUS_LABEL, ACTION_STATUS_OPTIONS, PLATFORM_LABEL, formatDateBR } from '../lib/format'
import { containsDocumentLink, DOC_LINK_IN_NOTE_MESSAGE } from '../lib/contextLinks'
import { CategoryBadge, PriorityBadge } from './PriorityBadge'
import { ActionStateBadge } from './ActionStateView'
import { ReasonChips, reasonFitsStatus, reasonMissing } from './ReasonChips'
import { Spinner } from './Spinner'

interface ActionLike extends ActionSharedState {
  id: string
  description: string
  rationale: string | null
  priority: Priority
  category: string
  platform?: AdPlatform | null
  campaign?: string | null
  // Só nas ações do gerente (aba Ações): quem registrou e como aparece no portal.
  origin?: 'ai' | 'manager'
  createdBy?: string | null
  clientText?: string | null
  clientHidden?: boolean
}

export function ActionCard({
  action,
  kind,
  reportId,
  clientId,
}: {
  action: ActionLike
  kind: 'optimization' | 'derived'
  reportId: string
  clientId?: string
}) {
  const byManager = action.origin === 'manager'
  const qc = useQueryClient()
  // Estado ÚNICO da ação: o formulário edita o registro do time, não uma cópia
  // pessoal. Salvar sobrescreve status e comentário para todo mundo.
  const [status, setStatus] = useState<ActionStatus>(action.state.status)
  const [note, setNote] = useState(action.state.note ?? '')
  const [reason, setReason] = useState<string | null>(action.state.reason ?? null)
  const [reviewAt, setReviewAt] = useState(action.state.reviewAt ?? '')
  const [noteError, setNoteError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        status,
        note: note.trim() ? note : null,
        reason: reasonFitsStatus(status, reason) ? reason : null,
        reviewAt: status === 'review' ? reviewAt : null,
      }
      return kind === 'optimization'
        ? actionsApi.logOptimization(action.id, body)
        : actionsApi.logDerived(action.id, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', reportId] }),
  })

  const handleSave = () => {
    if (containsDocumentLink(note)) {
      setNoteError(DOC_LINK_IN_NOTE_MESSAGE)
      return
    }
    setNoteError(null)
    mutation.mutate()
  }

  const changeStatus = (s: ActionStatus) => {
    setStatus(s)
    if (!reasonFitsStatus(s, reason)) setReason(null)
  }

  const dirty =
    status !== action.state.status ||
    note !== (action.state.note ?? '') ||
    (reason ?? null) !== (action.state.reason ?? null) ||
    (status === 'review' && reviewAt !== (action.state.reviewAt ?? ''))
  const canSave = dirty && (status !== 'review' || !!reviewAt) && !reasonMissing(status, reason)

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {byManager ? (
          <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">
            Do gerente{action.createdBy ? ` · ${action.createdBy}` : ''}
          </span>
        ) : (
          <PriorityBadge priority={action.priority} />
        )}
        <CategoryBadge category={action.category} />
        {(action.platform || action.campaign) && (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-500">
            {[action.platform ? PLATFORM_LABEL[action.platform] : null, action.campaign].filter(Boolean).join(' · ')}
          </span>
        )}
        <ActionStateBadge state={action.state} manualDate={byManager} />
      </div>
      <p className="font-medium text-slate-900">{action.description}</p>
      {action.rationale && (
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-medium">Por que: </span>
          {action.rationale}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {ACTION_STATUS_OPTIONS.map((opt) => (
          <label key={opt} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
            <input
              type="radio"
              name={`status-${action.id}`}
              checked={status === opt}
              onChange={() => changeStatus(opt)}
              className="text-brand-600 focus:ring-brand-500"
            />
            {ACTION_STATUS_LABEL[opt]}
          </label>
        ))}
      </div>

      <ReasonChips status={status} value={reason} onChange={setReason} />

      {status === 'review' && (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          Rever em:
          <input
            type="date"
            value={reviewAt}
            onChange={(e) => setReviewAt(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </label>
      )}

      <div className="mt-2 space-y-2">
        {action.state.by && (
          <p className="text-xs text-slate-400">
            Último registro por {action.state.by}
            {action.state.at ? ` em ${formatDateBR(action.state.at)}` : ''}
          </p>
        )}
        <textarea
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            if (noteError) setNoteError(null)
          }}
          rows={3}
          placeholder="Comentário da ação (visível para todos)"
          className="w-full resize-y rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
        {noteError && <p className="text-xs text-red-600">{noteError}</p>}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={mutation.isPending || !canSave}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending && <Spinner className="h-4 w-4 text-white" />}
            Salvar
          </button>
        </div>
      </div>

      {mutation.isError && (
        <p className="mt-1 text-xs text-red-600">Erro ao salvar. Tente novamente.</p>
      )}
      {mutation.isSuccess && !dirty && !mutation.isPending && (
        <p className="mt-1 text-xs text-green-600">Salvo ✓</p>
      )}

      {byManager && clientId && <ManagerActionFooter action={action} clientId={clientId} reportId={reportId} />}
    </div>
  )
}

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Erro ao salvar. Tente novamente.')

/** Ação do gerente: como aparece no portal do cliente (editar/esconder) e excluir. */
function ManagerActionFooter({ action, clientId, reportId }: { action: ActionLike; clientId: string; reportId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const refresh = () => qc.invalidateQueries({ queryKey: ['report', reportId] })
  const save = useMutation({
    mutationFn: (body: { clientText?: string | null; clientHidden?: boolean }) =>
      portalAdminApi.updateWork(clientId, 'optimization', action.id, body),
    onSuccess: () => {
      setEditing(null)
      refresh()
    },
  })
  const remove = useMutation({ mutationFn: () => actionsApi.removeManager(action.id), onSuccess: refresh })
  const done = action.state.status === 'done' || action.state.status === 'done_differently'

  return (
    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
      <div className="rounded-md bg-emerald-50/70 px-3 py-2 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">No portal do cliente</span>
          {editing == null && (
            <span className="flex gap-3 text-xs">
              {!action.clientHidden && (
                <button
                  type="button"
                  onClick={() => setEditing(action.clientText ?? '')}
                  className="font-medium text-brand-700 hover:underline"
                >
                  editar frase
                </button>
              )}
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate({ clientHidden: !action.clientHidden })}
                className="text-slate-500 hover:underline disabled:opacity-50"
              >
                {action.clientHidden ? 'mostrar ao cliente' : 'esconder do cliente'}
              </button>
            </span>
          )}
        </div>
        {editing != null ? (
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
            <input
              value={editing}
              onChange={(e) => setEditing(e.target.value)}
              maxLength={200}
              autoFocus
              aria-label="Frase para o cliente"
              placeholder="Em branco, a IA escreve sozinha."
              className="flex-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
            <span className="flex gap-2">
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate({ clientText: editing.trim() || null })}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                Salvar
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md px-2 py-1.5 text-sm text-slate-600 hover:bg-white"
              >
                Cancelar
              </button>
            </span>
          </div>
        ) : action.clientHidden ? (
          <p className="mt-1 text-slate-500">Escondida: o cliente não vê esta ação.</p>
        ) : (
          <p className="mt-1 text-slate-800">
            {action.clientText ? (
              <>“{action.clientText}”</>
            ) : (
              <span className="text-slate-500">A IA escreve a frase sozinha, ou escreva você em "editar frase".</span>
            )}
            {!done && <span className="ml-1 text-xs text-slate-500">Aparece quando a ação for marcada como feita.</span>}
          </p>
        )}
        {save.isError && <p className="mt-1 text-xs text-red-600">{errorText(save.error)}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {confirmDelete ? (
          <>
            <span className="text-slate-600">Excluir esta ação? Ela sai do histórico, do impacto e do portal.</span>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              className="rounded-md bg-red-600 px-2.5 py-1 font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Sim, excluir
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100"
            >
              Não
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-slate-400 hover:text-red-600 hover:underline"
          >
            Excluir ação
          </button>
        )}
        {remove.isError && <span className="text-red-600">{errorText(remove.error)}</span>}
      </div>
    </div>
  )
}
